import React, { useRef, useState } from 'react';
import { Feather, Download, Share2, Heart } from 'lucide-react';
import html2canvas from 'html2canvas';

// A Carta do Encontro — texto personalizado para o casal
// Uso: <CartaDoEncontro enc={enc} />

const CartaDoEncontro = ({ enc }) => {
  const ref = useRef(null);
  const [baixando, setBaixando] = useState(false);
  
  if (!enc) return null;

  const { nomeA, nomeB, padrao } = enc;

  // Gerar a carta baseada nos dados do encontro
  const gerarTexto = () => {
    const saudacao = `Queridos ${nomeA} e ${nomeB},`;
    
    // Abertura baseada no padrão do encontro - mais poética, sem repetir dados
    const aberturas = {
      encontro: `Vocês falam a mesma língua do coração. Quando dois corações vibram juntos assim, não precisam de tradução — só de presença.`,
      complemento: `O que falta em um, mora no outro. Não é coincidência — é a vida criando pontes entre vocês.`,
      tensao: `Vocês são diferentes — e é exatamente aí que mora a riqueza. A dança entre vocês é feita de passos distintos que, juntos, criam algo novo.`,
      equilibrio: `Dois corações inteiros que escolheram se encontrar. Não vieram preencher vazios, vieram somar presenças.`,
    };
    const abertura = aberturas[padrao] || aberturas.equilibrio;

    // Corpo simples - sem repetir pontos fortes/linguagem/desenvolver
    const corpo = `O mapa de vocês está desenhado acima. Mas o mapa não é o território — o amor real é feito de escolhas diárias, de gestos pequenos, de conversas que não terminam.`;

    // Fecho poético
    const fecho = `Cuidem um do outro como quem cuida de algo precioso — porque é.`;

    const assinatura = `— Os corações de ${nomeA} e ${nomeB}`;

    return { saudacao, abertura, corpo, fecho, assinatura };
  };

  const carta = gerarTexto();

  const baixar = async () => {
    if (!ref.current) return;
    setBaixando(true);
    try {
      const canvas = await html2canvas(ref.current, { backgroundColor: '#1b1530', scale: 2, useCORS: true, logging: false });
      const a = document.createElement('a');
      a.href = canvas.toDataURL('image/png');
      a.download = 'carta-do-encontro.png';
      a.click();
    } catch (e) { /* silencioso */ } finally { setBaixando(false); }
  };

  const compartilhar = async () => {
    const txt = `${nomeA} e ${nomeB} receberam a Carta do Encontro 💕\n\n"${enc.semente}"\n\nFaçam a Bússola do Encontro:`;
    const url = 'https://estacoes.ocoracaodetodasascoisas.com/encontro';
    try {
      if (navigator.share) await navigator.share({ title: 'A Carta do Encontro', text: txt, url });
      else { await navigator.clipboard.writeText(`${txt} ${url}`); alert('Carta copiada!'); }
    } catch (e) { console.log('Compartilhamento não disponível', e); }
  };

  return (
    <div className="my-8" data-testid="carta-do-encontro">
      <div className="flex justify-center mb-6">
        <div className="bg-gradient-to-r from-rose-900/40 via-amber-900/40 to-rose-900/40 px-7 py-3.5 rounded-full border border-rose-500/20">
          <div className="flex items-center gap-3">
            <Feather className="w-6 h-6 text-rose-300" />
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-extrabold text-white tracking-wide" style={{ fontFamily: "'Playfair Display', serif" }}>
                A Carta do Encontro
              </h2>
              <p className="text-white/50 text-xs mt-0.5">Escrita para vocês dois, a partir dos seus corações</p>
            </div>
          </div>
        </div>
      </div>

      <div ref={ref} className="relative rounded-3xl p-7 md:p-10 border border-rose-500/20 overflow-hidden"
           style={{ background: 'linear-gradient(160deg, rgba(69,26,3,0.55), rgba(49,16,40,0.5) 55%, rgba(27,21,48,0.6))' }}>
        
        {/* Ícone decorativo */}
        <div className="flex justify-center mb-6">
          <div className="w-16 h-16 rounded-full bg-gradient-to-br from-rose-500/30 to-amber-500/30 flex items-center justify-center border border-rose-400/20">
            <Heart className="w-8 h-8 text-rose-300" fill="currentColor" />
          </div>
        </div>

        {/* Saudação */}
        <p className="text-rose-100/90 text-xl md:text-2xl mb-6 text-center" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
          {carta.saudacao}
        </p>

        {/* Corpo da carta */}
        <div className="space-y-5" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          <p className="text-white/85 leading-relaxed text-[1.02rem]">{carta.abertura}</p>
          <p className="text-white/85 leading-relaxed text-[1.02rem]">{carta.corpo}</p>
          <p className="text-white/85 leading-relaxed text-[1.02rem]">{carta.desenvolvimento}</p>
          <p className="text-white/85 leading-relaxed text-[1.02rem]">{carta.fecho}</p>
        </div>

        {/* A semente */}
        <div className="mt-8 text-center">
          <p className="text-rose-300/60 text-xs uppercase tracking-widest mb-2">A semente do encontro de vocês</p>
          <p className="text-white text-xl md:text-2xl leading-snug" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
            "{enc.semente}"
          </p>
        </div>

        {/* Assinatura */}
        <p className="mt-7 text-right text-white/70 italic" style={{ fontFamily: "'Lora', Georgia, serif" }}>
          {carta.assinatura}
        </p>

        {/* Posfácio */}
        <p className="mt-6 pt-4 border-t border-white/10 text-white/50 text-sm italic leading-relaxed text-center">
          Esta carta é um espelho para a conversa de vocês — não um veredito. O amor real é feito de diálogo, não de diagnóstico.
        </p>
      </div>

      <div className="flex flex-col sm:flex-row justify-center items-center gap-3 mt-6">
        <button onClick={baixar} disabled={baixando} data-testid="baixar-carta-encontro"
          className="flex items-center gap-2.5 text-white border-none rounded-full px-8 py-4 text-base font-bold transition-all hover:scale-105"
          style={{ background: baixando ? 'rgba(236,72,153,0.4)' : 'linear-gradient(135deg, #EC4899, #F59E0B)', boxShadow: '0 4px 24px rgba(236,72,153,0.3)', cursor: baixando ? 'not-allowed' : 'pointer' }}>
          <Download className="w-5 h-5" /> {baixando ? 'Gerando…' : 'Baixar nossa carta'}
        </button>
        <button onClick={compartilhar}
          className="flex items-center gap-2.5 text-white/90 border border-white/20 rounded-full px-8 py-4 text-base font-bold transition-all hover:bg-white/10 hover:scale-105" style={{ cursor: 'pointer' }}>
          <Share2 className="w-5 h-5" /> Compartilhar
        </button>
      </div>
    </div>
  );
};

export default CartaDoEncontro;
